/**
 * Admin > RSI Momentum > Trade Review -- READ-ONLY. Explains every actual
 * TEK_RSI_MOMENTUM trade (paper or live) from stored data: why it entered,
 * how NIFTY moved, where stop/target were, where and why it exited, raw vs
 * filtered signals, blocked signals and data problems. Nothing here can
 * place, modify or cancel an order.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, FormControlLabel, Grid, MenuItem, Stack, Switch,
  Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from "@mui/material";
import rsiTradeReviewService from "@/services/rsiTradeReviewService";
import type { RsiTradeListFilters, RsiTradeListItem, RsiTradeReview } from "@/types";
import { exportToCsv } from "@/utils/csv";
import { extractErrorMessage } from "@/utils/errors";
import TradeDetailsPanel from "./TradeDetailsPanel";
import TradeReviewCharts from "./TradeReviewCharts";
import TradeReviewLightweightChart from "./TradeReviewLightweightChart";
import { C, csvRows, DEFAULT_TOGGLES, focusWindow, inr, istLabel, num, type Toggles, type Window } from "./tradeReviewChartData";

const EXIT_REASONS = ["TARGET_HIT", "STOP_LOSS_HIT", "BREAKEVEN_STOP_HIT", "TRAILING_STOP_HIT", "OPPOSITE_SIGNAL", "EOD_SQUARE_OFF", "FORCED_EXIT_BACKSTOP", "DAILY_LOSS_FLATTEN"];

const LEGEND: [string, string, string][] = [
  ["●", C.raw, "Raw Hilega-Milega RSI cross (before filters)"],
  ["▲", C.buy, "BUY — actual algo LONG entry"],
  ["▼", C.sell, "SELL — actual algo SHORT entry"],
  ["—", C.stop, "Stop-loss"],
  ["—", C.target, "Target"],
  ["◆", C.exit, "Actual exit"],
  ["✕", C.blocked, "Signal blocked by a filter"],
  ["▲", C.warning, "Missing / invalid data"],
];

export default function RsiTradeReviewPage() {
  const [params, setParams] = useSearchParams();
  const [filters, setFilters] = useState<RsiTradeListFilters>({});
  const [trades, setTrades] = useState<RsiTradeListItem[] | null>(null);
  const [listError, setListError] = useState<string | null>(null);
  const [review, setReview] = useState<RsiTradeReview | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toggles, setToggles] = useState<Toggles>(DEFAULT_TOGGLES);
  const [win, setWin] = useState<Window>({ start: 0, end: 0 });
  const selected = params.get("trade");

  const loadList = useCallback(async () => {
    setListError(null);
    try {
      setTrades(await rsiTradeReviewService.listTrades(filters));
    } catch (e) {
      setListError(extractErrorMessage(e));
      setTrades([]);
    }
  }, [filters]);

  useEffect(() => {
    void loadList();
  }, [loadList]);

  useEffect(() => {
    if (!selected) {
      setReview(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    rsiTradeReviewService
      .getTrade(selected)
      .then((r) => {
        if (cancelled) return;
        setReview(r);
        setWin(focusWindow(r));
      })
      .catch((e) => !cancelled && setError(extractErrorMessage(e)))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [selected]);

  const setFilter = (k: keyof RsiTradeListFilters) => (e: { target: { value: string } }) =>
    setFilters((f) => ({ ...f, [k]: e.target.value || undefined }));
  const toggle = (k: keyof Toggles, label: string) => (
    <FormControlLabel control={<Switch size="small" checked={toggles[k]} onChange={(e) => setToggles((t) => ({ ...t, [k]: e.target.checked }))} />} label={label} />
  );
  const totals = useMemo(() => {
    const closed = (trades ?? []).filter((t) => t.outcome !== "OPEN");
    return { n: closed.length, pnl: closed.reduce((a, t) => a + (t.realized_pnl ?? 0), 0), wins: closed.filter((t) => t.outcome === "PROFIT").length };
  }, [trades]);

  return (
    <Box sx={{ p: 3, maxWidth: 1600, mx: "auto" }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
        <Box>
          <Typography variant="h5" fontWeight={700}>RSI Momentum — Trade Review</Typography>
          <Typography variant="body2" color="text.secondary">
            Read-only. Every chart is built from the algo's stored trades, signals and NIFTY candles (IST) — nothing is simulated.
          </Typography>
        </Box>
        <Button component={RouterLink} to="/admin/strategies/rsi-momentum" variant="outlined" size="small">Back to RSI Momentum</Button>
      </Stack>

      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap alignItems="center">
            <TextField label="From" type="date" size="small" value={filters.date_from ?? ""} onChange={setFilter("date_from")} InputLabelProps={{ shrink: true }} />
            <TextField label="To" type="date" size="small" value={filters.date_to ?? ""} onChange={setFilter("date_to")} InputLabelProps={{ shrink: true }} />
            <TextField select label="Mode" size="small" value={filters.mode ?? ""} onChange={setFilter("mode")} sx={{ minWidth: 110 }}>
              <MenuItem value="">All</MenuItem><MenuItem value="PAPER">Paper</MenuItem><MenuItem value="LIVE">Live</MenuItem>
            </TextField>
            <TextField select label="Side" size="small" value={filters.side ?? ""} onChange={setFilter("side")} sx={{ minWidth: 110 }}>
              <MenuItem value="">All</MenuItem><MenuItem value="LONG">Long</MenuItem><MenuItem value="SHORT">Short</MenuItem>
            </TextField>
            <TextField select label="Result" size="small" value={filters.outcome ?? ""} onChange={setFilter("outcome")} sx={{ minWidth: 120 }}>
              <MenuItem value="">All</MenuItem><MenuItem value="PROFIT">Profit</MenuItem><MenuItem value="LOSS">Loss</MenuItem><MenuItem value="OPEN">Open</MenuItem>
            </TextField>
            <TextField select label="Exit reason" size="small" value={filters.exit_reason ?? ""} onChange={setFilter("exit_reason")} sx={{ minWidth: 190 }}>
              <MenuItem value="">All</MenuItem>
              {EXIT_REASONS.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
            </TextField>
            <Typography variant="body2" color="text.secondary" sx={{ ml: "auto" }}>
              {totals.n} closed · {totals.wins} profitable · net {inr(totals.pnl)}
            </Typography>
          </Stack>
          {listError && <Alert severity="error" sx={{ mt: 2 }}>{listError}</Alert>}
          {trades === null ? (
            <Box sx={{ p: 2, textAlign: "center" }}><CircularProgress size={24} /></Box>
          ) : trades.length === 0 ? (
            <Typography color="text.secondary" sx={{ mt: 2 }}>No TEK_RSI_MOMENTUM trades match these filters yet.</Typography>
          ) : (
            <Box sx={{ maxHeight: 260, overflow: "auto", mt: 2 }}>
              <Table size="small" stickyHeader data-testid="trade-list">
                <TableHead>
                  <TableRow>
                    {["Date", "Entry (IST)", "Side", "Mode", "Contract", "Entry", "Exit", "Exit reason", "P&L"].map((h) => <TableCell key={h}>{h}</TableCell>)}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {trades.map((t) => (
                    <TableRow key={t.id} hover selected={t.id === selected} sx={{ cursor: "pointer" }}
                      onClick={() => setParams({ trade: t.id })} data-testid={`trade-row-${t.id}`}>
                      <TableCell>{t.trade_date}</TableCell>
                      <TableCell>{t.entry_time_ist?.slice(11, 19)}</TableCell>
                      <TableCell><Chip size="small" label={t.side} color={t.side === "LONG" ? "success" : "error"} variant="outlined" /></TableCell>
                      <TableCell>{t.mode}</TableCell>
                      <TableCell>{t.contract}</TableCell>
                      <TableCell>{num(t.entry_price)}</TableCell>
                      <TableCell>{num(t.exit_price)}</TableCell>
                      <TableCell>{t.exit_reason ?? (t.outcome === "OPEN" ? "OPEN" : "—")}</TableCell>
                      <TableCell sx={{ color: (t.realized_pnl ?? 0) >= 0 ? "success.main" : "error.main", fontWeight: 600 }}>{inr(t.realized_pnl)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </CardContent>
      </Card>

      {!selected && <Alert severity="info">Select a trade above to see its chart and explanation.</Alert>}
      {loading && <Box sx={{ p: 4, textAlign: "center" }}><CircularProgress /></Box>}
      {error && <Alert severity="error" data-testid="review-error">{error}</Alert>}

      {review && !loading && (
        <Grid container spacing={2}>
          <Grid item xs={12} lg={8}>
            <Card variant="outlined">
              <CardContent>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
                  <Typography variant="subtitle1" fontWeight={700}>
                    NIFTY 5-minute · {review.summary.trade_date} · {review.summary.side} {istLabel(review.summary.entry_time_ist).slice(-12)}
                  </Typography>
                  <Box sx={{ flex: 1 }} />
                  {toggle("vwap", "VWAP")}
                  {toggle("ema20", "EMA(20)")}
                  {toggle("levels", "Stop / target")}
                  {toggle("rawCrosses", "Raw crosses")}
                  {toggle("rsi", "RSI panel")}
                  <Button size="small" variant="outlined"
                    onClick={() => exportToCsv(`rsi-momentum-trade-${review.summary.trade_date}-${review.summary.id.slice(0, 8)}.csv`, csvRows(review))}>
                    Export CSV
                  </Button>
                </Stack>
                <TradeReviewCharts review={review} toggles={toggles} window={win} onWindow={setWin} />
                <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mt: 1 }} data-testid="marker-legend">
                  {LEGEND.map(([g, color, text]) => (
                    <Typography key={text} variant="caption"><span style={{ color, fontWeight: 700 }}>{g}</span> {text}</Typography>
                  ))}
                </Stack>
              </CardContent>
            </Card>
            <Card variant="outlined" sx={{ mt: 2 }}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                  NIFTY 5-minute · TradingView-style view (IST)
                </Typography>
                <TradeReviewLightweightChart positionId={review.summary.id} initialReview={review} />
              </CardContent>
            </Card>
            {review.warnings.length > 0 && (
              <Alert severity="warning" sx={{ mt: 2 }} data-testid="data-warnings">
                <Typography variant="body2" fontWeight={600}>
                  Data quality ({review.data_quality.present_candles}/{review.data_quality.expected_candles} candles, source {review.data_quality.source})
                </Typography>
                {review.warnings.slice(0, 12).map((w, i) => (
                  <Typography key={i} variant="caption" sx={{ display: "block" }}>{w.time ? `${w.time} — ` : ""}{w.message}</Typography>
                ))}
                {review.warnings.length > 12 && <Typography variant="caption">…and {review.warnings.length - 12} more</Typography>}
              </Alert>
            )}
          </Grid>
          <Grid item xs={12} lg={4}>
            <TradeDetailsPanel review={review} />
            {review.timeline.length > 0 && (
              <Card variant="outlined" sx={{ mt: 2 }}>
                <CardContent>
                  <Typography variant="subtitle2" gutterBottom>Order timeline</Typography>
                  {review.timeline.map((e, i) => (
                    <Typography key={i} variant="caption" sx={{ display: "block" }}>
                      {e.time_ist?.slice(11, 19)} {e.intent} {e.side} {e.state} {e.filled_quantity}/{e.quantity}{e.avg_fill_price !== null ? ` @ ${inr(e.avg_fill_price)}` : ""}
                    </Typography>
                  ))}
                </CardContent>
              </Card>
            )}
          </Grid>
        </Grid>
      )}
    </Box>
  );
}
